import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { translations, type Language, type TranslationKeys } from './translations';

export type { Language };

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: TranslationKeys;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

const STORAGE_KEY = 'trackful_language';

// Map a raw locale string (e.g. "en-US", "de", "pt-BR") to a supported UI
// language, or null if unsupported. The app only ships the Brazilian
// Portuguese variant, so any "pt*" locale maps to pt-BR.
function mapLocaleToLanguage(raw: string | null | undefined): Language | null {
  if (!raw) return null;
  const loc = raw.toLowerCase();
  if (loc.startsWith('pt')) return 'pt-BR';
  const base = loc.split('-')[0];
  const supported = ['fr', 'en', 'de', 'es', 'ja', 'nl'];
  return supported.includes(base) ? (base as Language) : null;
}

// Resolve the initial UI language. Priority:
//   1. Explicit choice saved from Settings (localStorage) - always wins, so the
//      user can override auto-detection whenever they want.
//   2. Shopify admin locale, passed as ?locale=<locale> on the embedded app URL.
//   3. Browser language (also covers the WooCommerce dashboard).
//   4. French (product default).
function detectInitialLanguage(): Language {
  if (typeof window === 'undefined') return 'fr';
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved && saved in translations) return saved as Language;
  const params = new URLSearchParams(window.location.search);
  const detected =
    mapLocaleToLanguage(params.get('locale')) ??
    mapLocaleToLanguage(navigator.language);
  if (detected && detected in translations) return detected;
  return 'fr';
}

/**
 * Resolve the string table for a language, filling any gap from English.
 *
 * Why this exists: `t` used to be `translations[language]` with no fallback, and
 * the seven language tables are only kept in step by hand. A key added to `fr`
 * and forgotten in `ja` made `t.newSection` undefined, and the first
 * `t.newSection.title` threw - a white screen for that language, with nothing in
 * CI to catch it. The tables are two levels deep (section -> key), so a
 * two-level merge is enough and stays cheap.
 *
 * An English string in a Japanese dashboard is a small, honest degradation.
 * A blank page is not.
 */
function resolveTranslations(lang: Language): TranslationKeys {
  if (lang === 'en') return translations.en;
  const base = translations.en as unknown as Record<string, Record<string, unknown>>;
  const override = translations[lang] as unknown as Record<string, Record<string, unknown>>;
  const merged: Record<string, unknown> = {};
  for (const section of Object.keys(base)) {
    merged[section] = { ...base[section], ...(override[section] ?? {}) };
  }
  // Sections the language has and English does not: keep them rather than drop.
  for (const section of Object.keys(override)) {
    if (!(section in merged)) merged[section] = override[section];
  }
  return merged as unknown as TranslationKeys;
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  // Auto-detected on first load; an explicit Settings choice (persisted to
  // localStorage) takes precedence on subsequent loads via detectInitialLanguage.
  const [language, setLanguageState] = useState<Language>(detectInitialLanguage);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem(STORAGE_KEY, lang);
  }, []);

  useEffect(() => {
    // Set document language
    document.documentElement.lang = language;
  }, [language]);

  const t = useMemo(() => resolveTranslations(language), [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

export function useTranslation() {
  const { t } = useLanguage();
  return t;
}
